import React, { useCallback, useMemo } from 'react';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import {
  VisibilityOutlined as ViewIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  LockResetOutlined as ResetPasswordIcon,
} from '@mui/icons-material';
import { User } from '../../../types/user.types';
import { useUsersTable, UseUsersTableProps } from './UsersTable.hooks';
import {
  AccountText,
  NameText,
  RolesContainer,
  RoleChip,
  ExtraRolesChip,
  ActionsContainer,
  LoadingContainer,
} from './UsersTable.styles';
import { BaseTable, TableColumn } from '../../common/Table';
import { TableStatusChip } from '../../common/Table/TableStatusChip';
import { ActionButton } from '../../common/Table/Table.styles';
import { useHasFlagPermission } from '../../../hooks/useHasFlagPermission';
import { FlagSwitch } from '../../../components/ui';

export interface UsersTableProps extends UseUsersTableProps {
  loading: boolean;
  className?: string;
  onResetPassword?: (userId: string) => void;
  getPrimaryLocationName?: (primaryLocationId?: number | null) => string;
}

const DEFAULT_LOCATION_LABEL = '—';

const UsersTableComponent: React.FC<UsersTableProps> = ({
  users,
  loading,
  selectedUsers,
  setSelectedUsers,
  onViewUser,
  onEditUser,
  onDeleteUser,
  onBlockUserDirect,
  onUnblockUserDirect,
  className,
  onResetPassword,
  getPrimaryLocationName,
}) => {
  const canBlockUsers = useHasFlagPermission('settings', 'users-management', 'block');

  const resolveLocationName = useCallback(
    (primaryLocationId?: number | null) =>
      getPrimaryLocationName?.(primaryLocationId) ?? DEFAULT_LOCATION_LABEL,
    [getPrimaryLocationName],
  );

  const {
    getStatusDisplay,
    getStatusColor,
    getLockedDisplay,
    getLockedColor,
    formatDate,
    getUserName,
    getRoleNames,
    handleViewUser,
    handleEditUser,
    handleDeleteUser,
    handleSwitchChange,
    handleSelectItems,
    isBlocking,
    canDeleteUser,
    canEditUser,
  } = useUsersTable({
    users,
    selectedUsers,
    setSelectedUsers,
    onViewUser,
    onEditUser,
    onDeleteUser,
    onBlockUserDirect,
    onUnblockUserDirect,
  });

  const columns: TableColumn<User>[] = useMemo(
    () => [
      {
        id: 'email',
        label: 'Cuenta',
        sortAccessor: (item) => item.email,
        render: (item) => <AccountText>{item.email}</AccountText>,
      },
      {
        id: 'name',
        label: 'Nombre',
        sortAccessor: (item) => getUserName(item),
        render: (item) => <NameText>{getUserName(item)}</NameText>,
      },
      {
        id: 'phone',
        label: 'Teléfono',
        sortAccessor: (item) => item.phone,
        render: (item) => (
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{item.phone || 'N/A'}</Box>
        ),
      },
      {
        id: 'primaryLocationId',
        label: 'Sucursal Asignada',
        sortAccessor: (item) => resolveLocationName(item.primaryLocationId),
        render: (item) => (
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {resolveLocationName(item.primaryLocationId)}
          </Box>
        ),
      },
      {
        id: 'roles',
        label: 'Roles',
        sortAccessor: (item) => getRoleNames(item).join(','),
        render: (item) => {
          const roles = getRoleNames(item);
          if (!roles.length) {
            return <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Sin roles</Box>;
          }

          const visibleRoles = roles.slice(0, 2);
          const extraCount = roles.length - visibleRoles.length;

          return (
            <RolesContainer>
              {visibleRoles.map((roleName) => (
                <RoleChip key={roleName} label={roleName} size="small" />
              ))}
              {extraCount > 0 && <ExtraRolesChip label={`+${extraCount}`} size="small" />}
            </RolesContainer>
          );
        },
      },
      {
        id: 'status',
        label: 'Estado',
        sortAccessor: (item) => item.isActive,
        render: (item) => (
          <TableStatusChip
            label={getStatusDisplay(item.isActive)}
            color={getStatusColor(item.isActive)}
            visualStyle="quiet"
            withIcon={false}
          />
        ),
      },
      {
        id: 'locked',
        label: 'Bloqueado',
        sortAccessor: (item) => (item.lockedAt ? 1 : 0),
        render: (item) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {canBlockUsers && (
              <Box sx={{ mr: 1 }}>
                <FlagSwitch
                  checked={!item.lockedAt}
                  onChange={(e, checked) => handleSwitchChange(item, checked)}
                  disabled={isBlocking(item.id)}
                  activeColor={!item.lockedAt ? 'success' : undefined}
                />
              </Box>
            )}
            <TableStatusChip
              label={getLockedDisplay(item)}
              color={getLockedColor(item)}
              visualStyle="quiet"
              withIcon={false}
            />
          </Box>
        ),
      },
      {
        id: 'updatedAt',
        label: 'Última Modificación',
        sortAccessor: (item) => item.updatedAt,
        render: (item) => (
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {formatDate(item.updatedAt)}
          </Box>
        ),
      },
    ],
    [
      getStatusDisplay,
      getStatusColor,
      getLockedDisplay,
      getLockedColor,
      formatDate,
      getUserName,
      getRoleNames,
      resolveLocationName,
      canBlockUsers,
      handleSwitchChange,
      isBlocking,
    ],
  );

  const handleSelectItemsCallback = useCallback(
    (items: (string | number)[]) => handleSelectItems(items),
    [handleSelectItems],
  );

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return (
    <BaseTable<User>
      className={className}
      items={users}
      columns={columns}
      selectedItems={selectedUsers}
      onSelectItems={handleSelectItemsCallback}
      showCheckbox
      showActions
      getItemId={(item) => item.id}
      emptyStateTitle="No se encontraron usuarios"
      emptyStateSubtitle="Ajuste los filtros o cree un nuevo usuario."
      renderActions={(user) => (
        <ActionsContainer>
          {onViewUser && (
            <Tooltip title="Ver detalles" arrow>
              <ActionButton
                size="small"
                onClick={() => handleViewUser(user.id)}
                sx={{ color: 'info.main' }}
              >
                <ViewIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
          {onEditUser && canEditUser(user) && (
            <Tooltip title="Editar" arrow>
              <ActionButton
                size="small"
                onClick={() => handleEditUser(user.id)}
                sx={{ color: 'primary.main' }}
              >
                <EditIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
          {onResetPassword && canEditUser(user) && (
            <Tooltip title="Restablecer contraseña" arrow>
              <ActionButton
                size="small"
                onClick={() => onResetPassword(user.id)}
                sx={{ color: 'secondary.main' }}
              >
                <ResetPasswordIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
          {onDeleteUser && canDeleteUser(user) && (
            <Tooltip title="Eliminar usuario" arrow>
              <ActionButton
                size="small"
                onClick={() => handleDeleteUser(user.id)}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
        </ActionsContainer>
      )}
    />
  );
};

export default UsersTableComponent;
